import { useState, useCallback } from 'react';
import { useExamAuth } from '@/hooks/useExamAuth';

/** Local JWT decode — replaces the v3 SDK's `decodeToken`. */
function decodeToken<T = Record<string, unknown>>(token: string): T | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const normalized = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const pad = normalized.length % 4;
    const padded = pad ? normalized + '='.repeat(4 - pad) : normalized;
    return JSON.parse(atob(padded)) as T;
  } catch {
    return null;
  }
}
import {
  Shield,
  ShieldCheck,
  ShieldX,
  Search,
  RefreshCw,
  CheckCircle,
  XCircle,
  User,
  Key,
  Zap,
  Loader,
  ArrowRight,
  Database,
  Info,
} from 'lucide-react';

import {
  EXAM_PERMISSIONS,
  EXAM_PERMISSION_CATEGORIES,
  EXAM_RELATION_HIERARCHY,
  EXAM_SERVICE,
  useExamPermissions,
  PermissionGate,
} from '@/modules/permissions';

interface CheckResult {
  permission: string;
  resourceId?: string;
  allowed: boolean;
  source: string;
  latencyMs: number;
  error?: string;
}

export const PermissionTestPage = () => {
  // v4: Frontend SDK is JWT-only. Zanzibar runtime has moved to the Go
  // backend, so `isInitialized` is always false here and `checkPermission`
  // is a local wildcard match against the current JWT's permissions.
  const { user, roles, permissions: jwtPerms, accessToken } = useExamAuth();
  const isInitialized = false;
  const checkPermission = useCallback(
    async (perm: string, _resourceId?: string) => {
      if (roles.includes('system_admin') || roles.includes('super_admin')) return true;
      if (jwtPerms.includes('*:*:*') || jwtPerms.includes('*:*:*:*')) return true;
      return jwtPerms.includes(perm);
    },
    [roles, jwtPerms],
  );
  const clearCache = () => {};

  // Interactive checker state
  const [permInput, setPermInput] = useState('exam:create:exams:own');
  const [resourceInput, setResourceInput] = useState('');
  const [checking, setChecking] = useState(false);
  const [checkHistory, setCheckHistory] = useState<CheckResult[]>([]);

  // Batch check
  const allPermissions = Object.values(EXAM_PERMISSION_CATEGORIES).flat();
  const batch = useExamPermissions(allPermissions);

  // Decode JWT for display (the SDK gives us the token; we parse it here
  // purely for the debug UI — `jwtPermissions` is the same data the SDK
  // already surfaces via `useExamAuth().permissions`).
  const jwtClaims = accessToken ? decodeToken<Record<string, unknown>>(accessToken) : null;
  const jwtPermissions = jwtPerms;

  const handleCheck = useCallback(async () => {
    if (!permInput.trim()) return;
    setChecking(true);
    const start = performance.now();
    try {
      const allowed = await checkPermission(permInput.trim(), resourceInput.trim() || undefined);
      const latencyMs = Math.round(performance.now() - start);
      setCheckHistory(prev => [{
        permission: permInput.trim(),
        resourceId: resourceInput.trim() || undefined,
        allowed,
        source: isInitialized ? 'zanzibar' : 'jwt',
        latencyMs,
      }, ...prev].slice(0, 20));
    } catch (err) {
      const latencyMs = Math.round(performance.now() - start);
      setCheckHistory(prev => [{
        permission: permInput.trim(),
        resourceId: resourceInput.trim() || undefined,
        allowed: false,
        source: 'error',
        latencyMs,
        error: (err as Error).message,
      }, ...prev].slice(0, 20));
    }
    setChecking(false);
  }, [permInput, resourceInput, checkPermission, isInitialized]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Shield className="h-7 w-7 text-cyan-400" />
            Zanzibar Permission Tester
          </h1>
          <p className="text-gray-400 mt-1">
            Test and explore Zanzibar fine-grained permissions for the exam service
          </p>
        </div>
        <button
          onClick={() => { clearCache(); batch.refresh(); }}
          className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-gray-200 rounded-lg transition-colors"
        >
          <RefreshCw className="h-4 w-4" />
          Clear Cache
        </button>
      </div>

      {/* Section 1: User Info & Connection Status */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card-dark p-6">
          <h2 className="text-lg font-semibold text-white flex items-center gap-2 mb-4">
            <User className="h-5 w-5 text-cyan-400" />
            Current User
          </h2>
          <div className="space-y-3">
            <InfoRow label="User ID" value={(user as unknown as Record<string, unknown>)?.id as string || 'N/A'} mono />
            <InfoRow label="Email" value={(user as unknown as Record<string, unknown>)?.email as string || 'N/A'} />
            <InfoRow
              label="Roles"
              value={
                <div className="flex flex-wrap gap-1">
                  {(roles as string[] || []).map((r: string) => (
                    <span key={r} className="px-2 py-0.5 bg-cyan-500/20 text-cyan-300 rounded text-xs font-mono">
                      {r}
                    </span>
                  ))}
                  {(!roles || (roles as string[]).length === 0) && (
                    <span className="text-gray-500 text-sm">No roles</span>
                  )}
                </div>
              }
            />
            <InfoRow
              label="Service"
              value={
                <span className="px-2 py-0.5 bg-purple-500/20 text-purple-300 rounded text-xs font-mono">
                  {EXAM_SERVICE}
                </span>
              }
            />
          </div>
        </div>

        <div className="card-dark p-6">
          <h2 className="text-lg font-semibold text-white flex items-center gap-2 mb-4">
            <Database className="h-5 w-5 text-cyan-400" />
            Connection Status
          </h2>
          <div className="space-y-3">
            <InfoRow
              label="Zanzibar Client"
              value={
                <span className={`flex items-center gap-1.5 text-sm ${isInitialized ? 'text-green-400' : 'text-yellow-400'}`}>
                  <span className={`h-2 w-2 rounded-full ${isInitialized ? 'bg-green-400' : 'bg-yellow-400'}`} />
                  {isInitialized ? 'Connected' : 'Not Initialized'}
                </span>
              }
            />
            <InfoRow
              label="Permission Source"
              value={
                <span className="text-sm text-gray-300">
                  {isInitialized ? 'Zanzibar (REST API) with JWT fallback' : 'JWT claims only'}
                </span>
              }
            />
            <InfoRow
              label="JWT Permissions"
              value={
                <span className="text-sm text-gray-300">
                  {jwtPermissions.length} permission{jwtPermissions.length !== 1 ? 's' : ''} in token
                </span>
              }
            />
            {jwtPermissions.length > 0 && (
              <div className="mt-2 p-3 bg-gray-800/50 rounded-lg max-h-32 overflow-y-auto">
                {jwtPermissions.map((p, i) => (
                  <div key={i} className="text-xs font-mono text-gray-400">{p}</div>
                ))}
              </div>
            )}
            {jwtClaims && (
              <InfoRow
                label="Token Issuer"
                value={
                  <span className="text-xs font-mono text-gray-400">
                    {(jwtClaims as Record<string, unknown>).iss as string || 'unknown'}
                  </span>
                }
              />
            )}
          </div>
        </div>
      </div>

      {/* Section 2: Interactive Permission Checker */}
      <div className="card-dark p-6">
        <h2 className="text-lg font-semibold text-white flex items-center gap-2 mb-4">
          <Search className="h-5 w-5 text-cyan-400" />
          Interactive Permission Checker
        </h2>
        <div className="flex flex-col sm:flex-row gap-3 mb-4">
          <div className="flex-1">
            <label className="block text-xs text-gray-400 mb-1">Permission String</label>
            <input
              type="text"
              value={permInput}
              onChange={e => setPermInput(e.target.value)}
              placeholder="exam:create:exams:own"
              className="input-dark w-full"
              onKeyDown={e => e.key === 'Enter' && handleCheck()}
            />
          </div>
          <div className="w-full sm:w-48">
            <label className="block text-xs text-gray-400 mb-1">Resource ID (optional)</label>
            <input
              type="text"
              value={resourceInput}
              onChange={e => setResourceInput(e.target.value)}
              placeholder="uuid..."
              className="input-dark w-full"
              onKeyDown={e => e.key === 'Enter' && handleCheck()}
            />
          </div>
          <div className="flex items-end">
            <button
              onClick={handleCheck}
              disabled={checking || !permInput.trim()}
              className="flex items-center gap-2 px-6 py-2 bg-cyan-600 hover:bg-cyan-500 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
            >
              {checking ? <Loader className="h-4 w-4 animate-spin" /> : <Zap className="h-4 w-4" />}
              Check
            </button>
          </div>
        </div>

        {/* Quick fill buttons */}
        <div className="flex flex-wrap gap-2 mb-4">
          <span className="text-xs text-gray-500 self-center">Quick:</span>
          {[
            EXAM_PERMISSIONS.CREATE_EXAM,
            EXAM_PERMISSIONS.GRADE_ALL_ATTEMPTS,
            EXAM_PERMISSIONS.MONITOR_ALL_EXAMS,
            EXAM_PERMISSIONS.MANAGE_SETTINGS,
            EXAM_PERMISSIONS.SUPERADMIN,
          ].map(p => (
            <button
              key={p}
              onClick={() => setPermInput(p)}
              className="px-2 py-1 text-xs bg-gray-700 hover:bg-gray-600 text-gray-300 rounded transition-colors font-mono"
            >
              {p}
            </button>
          ))}
        </div>

        {/* Check History */}
        {checkHistory.length > 0 && (
          <div className="space-y-2">
            <div className="text-xs text-gray-500 uppercase tracking-wider">Recent Checks</div>
            <div className="space-y-1 max-h-64 overflow-y-auto">
              {checkHistory.map((result, i) => (
                <div
                  key={i}
                  className={`flex items-center justify-between p-3 rounded-lg ${
                    result.allowed ? 'bg-green-500/10 border border-green-500/20' : 'bg-red-500/10 border border-red-500/20'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    {result.allowed ? (
                      <CheckCircle className="h-4 w-4 text-green-400 flex-shrink-0" />
                    ) : (
                      <XCircle className="h-4 w-4 text-red-400 flex-shrink-0" />
                    )}
                    <div>
                      <span className="font-mono text-sm text-gray-200">{result.permission}</span>
                      {result.resourceId && (
                        <span className="text-xs text-gray-500 ml-2">({result.resourceId})</span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-3 text-xs">
                    <span className={`px-2 py-0.5 rounded ${
                      result.source === 'zanzibar' ? 'bg-purple-500/20 text-purple-300' :
                      result.source === 'error' ? 'bg-red-500/20 text-red-300' :
                      'bg-gray-500/20 text-gray-300'
                    }`}>
                      {result.source}
                    </span>
                    <span className="text-gray-500">{result.latencyMs}ms</span>
                    {result.error && (
                      <span className="text-red-400 truncate max-w-[200px]" title={result.error}>
                        {result.error}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Section 3: Batch Permission Check */}
      <div className="card-dark p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-white flex items-center gap-2">
            <Key className="h-5 w-5 text-cyan-400" />
            Exam Permissions Matrix
          </h2>
          <div className="flex items-center gap-3 text-sm">
            <span className="text-green-400">{batch.allowedCount} allowed</span>
            <span className="text-gray-500">|</span>
            <span className="text-red-400">{batch.deniedCount} denied</span>
            <button
              onClick={batch.refresh}
              className="p-1.5 hover:bg-gray-700 rounded transition-colors"
              title="Refresh"
            >
              <RefreshCw className={`h-4 w-4 text-gray-400 ${batch.loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>

        {batch.error && (
          <div className="mb-4 p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-lg text-yellow-300 text-sm flex items-center gap-2">
            <Info className="h-4 w-4 flex-shrink-0" />
            Zanzibar unavailable, showing JWT fallback results: {batch.error.message}
          </div>
        )}

        <div className="space-y-4">
          {Object.entries(EXAM_PERMISSION_CATEGORIES).map(([category, perms]) => (
            <div key={category}>
              <h3 className="text-sm font-medium text-gray-400 mb-2">{category}</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                {perms.map(perm => {
                  const allowed = batch.isAllowed(perm);
                  return (
                    <div
                      key={perm}
                      className={`flex items-center justify-between p-2.5 rounded-lg border ${
                        batch.loading
                          ? 'bg-gray-800/50 border-gray-700'
                          : allowed
                          ? 'bg-green-500/5 border-green-500/20'
                          : 'bg-red-500/5 border-red-500/20'
                      }`}
                    >
                      <span className="font-mono text-xs text-gray-300 truncate mr-2" title={perm}>
                        {perm}
                      </span>
                      {batch.loading ? (
                        <Loader className="h-3.5 w-3.5 text-gray-500 animate-spin flex-shrink-0" />
                      ) : allowed ? (
                        <CheckCircle className="h-3.5 w-3.5 text-green-400 flex-shrink-0" />
                      ) : (
                        <XCircle className="h-3.5 w-3.5 text-red-400 flex-shrink-0" />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Section 4: PermissionGate Demo */}
      <div className="card-dark p-6">
        <h2 className="text-lg font-semibold text-white flex items-center gap-2 mb-4">
          <ShieldCheck className="h-5 w-5 text-cyan-400" />
          {'<PermissionGate>'} Component Demo
        </h2>
        <p className="text-sm text-gray-400 mb-4">
          These blocks are wrapped in {'<PermissionGate>'} — they render only if the permission is granted.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <GateDemo
            permission={EXAM_PERMISSIONS.CREATE_EXAM}
            label="Create Exam"
            description="This button appears only if you can create exams."
          />
          <GateDemo
            permission={EXAM_PERMISSIONS.GRADE_ALL_ATTEMPTS}
            label="Grade All Attempts"
            description="This panel appears only if you can grade all attempts."
          />
          <GateDemo
            permission={EXAM_PERMISSIONS.MANAGE_SETTINGS}
            label="Manage Settings"
            description="Settings access requires admin permissions."
          />
          <GateDemo
            permission={EXAM_PERMISSIONS.MONITOR_ALL_EXAMS}
            label="Monitor All Exams"
            description="Proctoring monitor requires monitoring permissions."
          />
        </div>
      </div>

      {/* Section 5: Zanzibar Relations Reference */}
      <div className="card-dark p-6">
        <h2 className="text-lg font-semibold text-white flex items-center gap-2 mb-4">
          <Shield className="h-5 w-5 text-cyan-400" />
          Zanzibar Relations — Exam Namespace
        </h2>
        <p className="text-sm text-gray-400 mb-4">
          The exam service uses these Zanzibar relations for resource-level access control.
          Higher relations inherit permissions from lower ones.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {EXAM_RELATION_HIERARCHY.map((rel) => (
            <div
              key={rel.relation}
              className="p-4 bg-gray-800/50 border border-gray-700 rounded-lg hover:border-cyan-500/30 transition-colors"
            >
              <div className="flex items-center gap-2 mb-2">
                <Shield className="h-4 w-4 text-cyan-400" />
                <span className="font-semibold text-white">{rel.label}</span>
                <span className="text-xs font-mono text-gray-500">{rel.relation}</span>
              </div>
              <p className="text-sm text-gray-400 mb-3">{rel.description}</p>
              {rel.implies.length > 0 && (
                <div className="flex items-center gap-1 flex-wrap">
                  <span className="text-xs text-gray-500">Implies:</span>
                  {rel.implies.map((imp) => (
                    <span key={imp} className="flex items-center gap-0.5">
                      <ArrowRight className="h-3 w-3 text-gray-600" />
                      <span className="text-xs font-mono text-cyan-300/70">{imp}</span>
                    </span>
                  ))}
                </div>
              )}
              {rel.implies.length === 0 && (
                <span className="text-xs text-gray-600">No inherited relations</span>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Usage Examples */}
      <div className="card-dark p-6">
        <h2 className="text-lg font-semibold text-white flex items-center gap-2 mb-4">
          <Info className="h-5 w-5 text-cyan-400" />
          Usage Examples
        </h2>
        <div className="space-y-4">
          <CodeBlock
            title="PermissionGate — Conditional Rendering"
            code={`import { PermissionGate, EXAM_PERMISSIONS } from '@/modules/permissions';

<PermissionGate permission={EXAM_PERMISSIONS.CREATE_EXAM}>
  <button>Create Exam</button>
</PermissionGate>

<PermissionGate
  permissions={[EXAM_PERMISSIONS.GRADE_ATTEMPTS, EXAM_PERMISSIONS.GRADE_ALL_ATTEMPTS]}
  mode="any"
  fallback={<p>No grading access</p>}
>
  <GradingPanel />
</PermissionGate>`}
          />
          <CodeBlock
            title="useExamPermission — Hook"
            code={`import { useExamPermission, EXAM_PERMISSIONS } from '@/modules/permissions';

const { allowed, loading, source } = useExamPermission(EXAM_PERMISSIONS.CREATE_EXAM);
// source: 'zanzibar' | 'jwt' | 'superadmin'`}
          />
          <CodeBlock
            title="PermissionGuardedRoute — Route Protection"
            code={`import { PermissionGuardedRoute, EXAM_PERMISSIONS } from '@/modules/permissions';

<Route path="/admin/settings" element={
  <PermissionGuardedRoute permission={EXAM_PERMISSIONS.MANAGE_SETTINGS}>
    <SettingsPage />
  </PermissionGuardedRoute>
} />`}
          />
        </div>
      </div>
    </div>
  );
};

// Helper components
function InfoRow({ label, value, mono }: { label: string; value: React.ReactNode; mono?: boolean }) {
  return (
    <div className="flex items-start justify-between gap-4">
      <span className="text-sm text-gray-400 whitespace-nowrap">{label}</span>
      <div className={`text-sm text-gray-200 text-right ${mono ? 'font-mono text-xs' : ''}`}>{value}</div>
    </div>
  );
}

function GateDemo({ permission, label, description }: { permission: string; label: string; description: string }) {
  return (
    <div className="p-4 border border-gray-700 rounded-lg">
      <div className="text-xs font-mono text-gray-500 mb-2">{permission}</div>
      <PermissionGate
        permission={permission}
        fallback={
          <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
            <ShieldX className="h-4 w-4 text-red-400" />
            <div>
              <div className="text-sm text-red-300 font-medium">{label}: Denied</div>
              <div className="text-xs text-red-400/70">{description}</div>
            </div>
          </div>
        }
        loading={
          <div className="flex items-center gap-2 p-3 bg-gray-800 rounded-lg">
            <Loader className="h-4 w-4 text-gray-400 animate-spin" />
            <span className="text-sm text-gray-400">Checking...</span>
          </div>
        }
      >
        <div className="flex items-center gap-2 p-3 bg-green-500/10 border border-green-500/20 rounded-lg">
          <ShieldCheck className="h-4 w-4 text-green-400" />
          <div>
            <div className="text-sm text-green-300 font-medium">{label}: Granted</div>
            <div className="text-xs text-green-400/70">{description}</div>
          </div>
        </div>
      </PermissionGate>
    </div>
  );
}

function CodeBlock({ title, code }: { title: string; code: string }) {
  return (
    <div>
      <div className="text-sm font-medium text-gray-300 mb-2">{title}</div>
      <pre className="p-4 bg-gray-900 border border-gray-700 rounded-lg overflow-x-auto">
        <code className="text-xs font-mono text-gray-300 whitespace-pre">{code}</code>
      </pre>
    </div>
  );
}
